import React, { useState } from 'react';
import logo from './logo.svg';
import './App.css';

function App() {
  type FormData = {
    name: string;
    email: string;
    phone?: string;
    destination: string;
    departDate?: string;
    returnDate?: string;
    budget?: string;
    message?: string;
  };

  const [form, setForm] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    destination: '',
    departDate: '',
    returnDate: '',
    budget: '',
    message: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState<string>('');

  const validate = (data: FormData) => {
    const e: Record<string, string> = {};
    if (!data.name.trim()) e.name = 'Name is required';
    if (!data.email.trim()) e.email = 'Email is required';
    else {
      // simple email check
      const re = /\S+@\S+\.\S+/;
      if (!re.test(data.email)) e.email = 'Enter a valid email';
    }
    if (!data.destination.trim()) e.destination = 'Preferred destination is required';
    return e;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError('');
    const validation = validate(form);
    if (Object.keys(validation).length) {
      setErrors(validation);
      setSubmitted(false);
      return;
    }

    try {
      const res = await fetch('/api/v1/email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        const json = await res.json().catch(() => ({ error: res.statusText }));
        setServerError(json.error || 'Server error');
        setSubmitted(false);
        return;
      }

      setSubmitted(true);
      setForm({
        name: '',
        email: '',
        phone: '',
        destination: '',
        departDate: '',
        returnDate: '',
        budget: '',
        message: '',
      });
      setErrors({});
    } catch (err) {
      setServerError('Network error — could not reach server');
      setSubmitted(false);
    }
  };

  return (
    <div className="App landing">
      <header className="site-header">
        <div className="brand">
          <img src={logo} className="brand-logo" alt="Daydream and Depart logo" />
          <div>
            <h1>Daydream &amp; Depart</h1>
            <p className="tagline">Curated travel & unforgettable journeys</p>
          </div>
        </div>
      </header>

      <main className="hero">
        <section className="hero-content">
          <h2>Turn your daydreams into departures</h2>
          <p>Specializing in bespoke vacations, family adventures, honeymoons, and corporate travel. Tell us what you imagine and we'll create it.</p>
          <ul className="features">
            <li>Personalized itineraries</li>
            <li>Exclusive deals &amp; insider access</li>
            <li>24/7 travel support</li>
          </ul>
        </section>

        <aside className="contact-card">
          <h3>Request more information</h3>

          {submitted && <div className="success">Thanks — we'll be in touch shortly!</div>}
          {serverError && <div className="error" style={{ background: '#fff0f0', padding: 8, borderRadius: 6 }}>{serverError}</div>}

          <form onSubmit={handleSubmit} noValidate>
            <label>
              Full name *
              <input name="name" value={form.name} onChange={handleChange} />
              {errors.name && <span className="error">{errors.name}</span>}
            </label>

            <label>
              Email *
              <input name="email" value={form.email} onChange={handleChange} />
              {errors.email && <span className="error">{errors.email}</span>}
            </label>

            <label>
              Phone
              <input name="phone" value={form.phone} onChange={handleChange} />
            </label>

            <label>
              Preferred destination *
              <input name="destination" value={form.destination} onChange={handleChange} placeholder="City, country or region" />
              {errors.destination && <span className="error">{errors.destination}</span>}
            </label>

            <div className="date-row">
              <label>
                Depart
                <input type="date" name="departDate" value={form.departDate} onChange={handleChange} />
              </label>
              <label>
                Return
                <input type="date" name="returnDate" value={form.returnDate} onChange={handleChange} />
              </label>
            </div>

            <label>
              Budget (approx.)
              <select name="budget" value={form.budget} onChange={handleChange}>
                <option value="">Select</option>
                <option value="budget">Under $1,500</option>
                <option value="mid"> $1,500 - $5,000</option>
                <option value="luxury">Over $5,000</option>
              </select>
            </label>

            <label>
              Message
              <textarea name="message" value={form.message} onChange={handleChange} rows={4} />
            </label>

            <button type="submit" className="btn-primary">Request Info</button>
          </form>

          <p className="privacy-note">We respect your privacy. Your info will only be used to contact you about travel planning.</p>
        </aside>
      </main>

      <footer className="site-footer">
        <small>© {new Date().getFullYear()} Daydream &amp; Depart — Travel planning made personal</small>
      </footer>
    </div>
  );
}

export default App;
